import { KeyboardArrowDown, Clear } from "@mui/icons-material";
import { FormControl, Select, MenuItem, Typography, IconButton, Box, Chip } from "@mui/material";
import React, { Fragment } from "react";
import theme from "../../theme";

const CommonSelect = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    placeholder = "Select an option",
    options = [],
    error,
    displayEmpty = true,
    disabled,
    allowClear = false, // New prop to enable/clear functionality,
    width,
    multiple
}) => {
    // Default arrow component that properly handles open/close state
    const DefaultArrow = (props) => {
        return (
            <KeyboardArrowDown
                {...props}
                sx={{
                    fontSize: 30,
                    transition: 'transform 0.2s',
                    color: theme.palette.background.dark,
                    transform: props.open ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
            />
        );
    };

    // Custom arrow component that includes clear button
    const ClearableArrow = (props) => {
        const showClearButton = allowClear && value && !disabled;

        return (
            <div style={{ position: 'relative' }}>
                {showClearButton && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onChange) {
                                const event = {
                                    target: {
                                        name: name,
                                        value: ''
                                    }
                                };
                                onChange(event);
                            }
                        }}
                        sx={{
                            position: 'absolute',
                            right: '30px', // Position before the arrow
                            top: '50%',
                            transform: 'translateY(-50%)',
                            padding: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                        }}
                    >
                        <Clear
                            sx={{
                                fontSize: 18,
                                color: 'rgba(0, 0, 0, 0.54)',
                                '&:hover': {
                                    color: 'rgba(0, 0, 0, 0.87)',
                                }
                            }}
                        />
                    </IconButton>
                )}
                <DefaultArrow {...props} />
            </div>
        );
    };

    return (
        <Fragment>
            {label && (
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'text.main',
                        mb: 0.8
                    }}
                >
                    {label}
                </Typography>
            )}
            <FormControl
                fullWidth
                size="small"
                sx={{
                    minWidth: 'auto',
                    position: 'relative', // Added for positioning
                    width: width || '100%',
                    '& .MuiOutlinedInput-root': {
                        height: '39px',
                        background: 'white',
                        '&:hover fieldset': {
                            borderColor: 'lightgray',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'lightgray',
                            borderWidth: '1px',
                        },
                        '& .MuiSelect-icon': {
                            right: '8px',
                            pointerEvents: 'none' // This allows clicks to pass through to the select
                        }
                    }
                }}
            >
                <Select
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    displayEmpty={displayEmpty}
                    disabled={disabled}
                    inputProps={{
                        'aria-label': label || placeholder,
                        name: name
                    }}
                    multiple={multiple}
                    sx={{
                        height: '38px',
                        fontSize: '14px',
                        fontWeight: 600,
                        borderRadius: 1,
                        '& .MuiSelect-select': {
                            padding: '8px 32px 8px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            color: value ? 'inherit' : '#9e9e9e',
                            paddingRight: allowClear && value && !disabled ? '60px' : '32px' // Adjust padding for clear button
                        },
                        // Ensure the select is clickable
                        '& .MuiOutlinedInput-input': {
                            cursor: 'pointer'
                        }
                    }}
                    MenuProps={{
                        sx: {
                            '& .MuiMenuItem-root': {
                                fontSize: '14px',
                                '&.Mui-selected': {
                                    backgroundColor: '#f5f5f5',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0',
                                    },
                                    '&.Mui-focusVisible': {
                                        backgroundColor: '#f5f5f5',
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#fafafa',
                                }
                            }
                        },
                        PaperProps: {
                            sx: {
                                marginTop: '4px',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px',
                            }
                        }
                    }}
                    IconComponent={ClearableArrow} // Use the combined arrow component
                    renderValue={(selected) => {
                        if (!selected || selected === '') {
                            return (
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#9e9e9e', fontSize: '15px' }}
                                >
                                    {placeholder}
                                </Typography>
                            );
                        }

                        if (multiple) {
                            return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} />
                                    ))}
                                </Box>
                            )
                        }
                        const selectedOption = options.find(option => option.value === selected);
                        return selectedOption ? selectedOption.label : selected;
                    }}
                >
                    {/* Placeholder MenuItem */}
                    {displayEmpty && (
                        <MenuItem value="" disabled sx={{ display: 'none' }}>
                            {placeholder}
                        </MenuItem>
                    )}

                    {/* Optional: Add a clear option in the dropdown */}
                    {allowClear && value && (
                        <MenuItem
                            value=""
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            sx={{
                                fontSize: '14px',
                                color: '#d32f2f',
                                '&:hover': {
                                    backgroundColor: '#ffebee',
                                }
                            }}
                        >
                            <Clear sx={{ fontSize: 18, marginRight: 1 }} />
                            Clear Selection
                        </MenuItem>
                    )}

                    {/* Render options */}
                    {options.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                fontSize: '14px',
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {error && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{
                        fontSize: '12px',
                        display: 'block',
                        mt: 0.5
                    }}
                >
                    {error}
                </Typography>
            )}
        </Fragment>
    )
}

export default CommonSelect;